import { motion } from 'framer-motion';
import { Flame, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { DOORDASH_URL, UBEREATS_URL, PHONE_NUMBER } from '../data/menu';

export function BuildYourGrill() {
  const { t, isRTL } = useLanguage();
  const meats = t('buildGrill.meats');
  const sides = t('buildGrill.sideOptions');

  return (
    <section
      id="custom-platters"
      className="py-20 px-4 bg-gradient-to-b from-white to-[#faf7f2]"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="build-your-grill-section"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="w-14 h-14 bg-[#6B1C23]/10 rounded-full flex items-center justify-center mx-auto mb-5"
          >
            <Flame className="w-7 h-7 text-[#6B1C23]" />
          </motion.div>
          <h2 className="font-cormorant text-4xl sm:text-5xl font-bold text-[#2C2C2C] mb-3" data-testid="custom-platters-title">
            {t('buildGrill.title')}
          </h2>
          <p className="font-manrope text-base text-[#666] max-w-xl mx-auto">
            {t('buildGrill.subtitle')}
          </p>
        </motion.div>

        {/* Meats & Sides Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Meats */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg p-8 shadow-sm border border-[#e8e0d4]"
          >
            <h3 className="font-cormorant text-2xl font-bold text-[#6B1C23] mb-5 text-center">
              {isRTL ? 'اللحوم' : 'Meats'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Array.isArray(meats) && meats.map((meat, i) => (
                <motion.div
                  key={`meat-${i}`}
                  whileHover={{ scale: 1.03, backgroundColor: '#faf7f2' }}
                  className="px-4 py-3 rounded-md border border-[#e8e0d4] text-center font-manrope text-sm text-[#444] cursor-default transition-colors"
                >
                  {meat}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Sides */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg p-8 shadow-sm border border-[#e8e0d4]"
          >
            <h3 className="font-cormorant text-2xl font-bold text-[#6B1C23] mb-5 text-center">
              {isRTL ? 'الأطباق الجانبية' : 'Sides'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Array.isArray(sides) && sides.map((side, i) => (
                <motion.div
                  key={`side-${i}`}
                  whileHover={{ scale: 1.03, backgroundColor: '#faf7f2' }}
                  className="px-4 py-3 rounded-md border border-[#e8e0d4] text-center font-manrope text-sm text-[#444] cursor-default transition-colors"
                >
                  {side}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Button
            asChild
            className="bg-[#6B1C23] text-white hover:bg-[#8B2430] transition-all py-6 px-8 font-manrope"
            data-testid="order-doordash-btn"
          >
            <a href={DOORDASH_URL} target="_blank" rel="noopener noreferrer">
              {t('buildGrill.orderNow')} — DoorDash
            </a>
          </Button>
          <Button
            asChild
            className="bg-[#142328] text-white hover:bg-[#1f3640] transition-all py-6 px-8 font-manrope"
            data-testid="order-ubereats-btn"
          >
            <a href={UBEREATS_URL} target="_blank" rel="noopener noreferrer">
              {t('buildGrill.orderNow')} — Uber Eats
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-[#6B1C23] text-[#6B1C23] hover:bg-[#6B1C23]/10 transition-all py-6 px-8 font-manrope"
            data-testid="call-to-order-btn"
          >
            <a href={`tel:${PHONE_NUMBER}`} className="flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              {t('buildGrill.callToOrder')}
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
